/**
 * The Timeline view (T5.1 to T5.3) — the generator's `#view-timeline`, rendered
 * from state instead of built with `createElement` and patched.
 *
 * Every element is a function of `buildTimeline`'s result, so there is nothing to
 * go stale after an edit and no query into the DOM. `schedule`, `holidays` and
 * `leave` arrive as three siblings of `model`; none is derived from another.
 *
 * Words: the nine display terms come from `useTerms()`, including the plural and
 * the singular of the row captions ("1 room", "12 rooms"). The remaining English
 * here — "Delivery timeline", "Contingency", "Public holiday", "Leave", "Today" —
 * is not one of the nine. What the generator held as static, plan-specific prose (the two-person
 * summary and the effort notes) is not in the data and is not ported; see the
 * report to the Lead.
 */
import { useMemo, useState } from 'react';
import type { BlueprintModel } from '@blueprint/shared';
import { useTerms } from '../../lib/terms';
import type { HolidayRow, LeaveRow } from '../../lib/blueprints';
import {
  PARTY_LABEL,
  buildTimeline,
  fmtDay,
  fmtDayYear,
  type ScheduleRowWithId,
  type TimelineBar,
  type TimelineLayout,
  type TimelineRow,
} from './layout';
import { RoomDeliverables } from './RoomDeliverables';
import { barKey, useScheduleDrag, type TimelineEditing } from './useScheduleDrag';

export type { TimelineEditing } from './useScheduleDrag';

function countLabel(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function Legend({ layout }: { layout: TimelineLayout }) {
  const terms = useTerms();
  const room = terms.noun('room');
  return (
    <div className="tl-legend">
      {/* The two parties (§ 7.7). Colour is never the only difference between
          them: the Consultant bar is plain and always the UPPER lane, the Client
          bar is hatched and always the lower one, on every row and in both
          themes. The swatches here are the bars themselves, so the legend
          carries the same two cues. */}
      {layout.parties.map((p) => (
        <span key={p}>
          <i className={`tl-key ${p}`} />
          {PARTY_LABEL[p]}
          {p === 'consultant' ? ' · upper' : ' · lower, hatched'}
        </span>
      ))}
      {layout.shutdown ? (
        <span>
          <i style={{ background: 'rgba(190,150,70,.28)', border: '1px dashed rgba(214,176,94,.6)' }} />
          {layout.shutdown.label}
          {layout.shutdown.label.includes('·') ? '' : ` · ${fmtDay(layout.shutdown.start)} – ${fmtDay(layout.shutdown.end)}`}
        </span>
      ) : null}
      {layout.hasLeave ? (
        <span>
          {/* The swatch was inline dark-theme rgba, which did not change with the
              theme; it is a class now so it matches the bar it stands for. */}
          <i className="leave" />
          Leave, one person
        </span>
      ) : null}
      {layout.contingency ? (
        <span>
          <i style={{ background: 'rgba(95,158,106,.22)', border: '1px solid rgba(95,158,106,.5)' }} />
          {layout.contingency.label} · {fmtDay(layout.contingency.start)} – {fmtDay(layout.contingency.end)}
        </span>
      ) : null}
      <span>
        <i className="tl-dot done" style={{ borderRadius: '50%' }} />
        {room} complete
      </span>
      <span>
        <i className="tl-dot wip" style={{ borderRadius: '50%' }} />
        {room} in progress
      </span>
      {layout.hasHolidays ? (
        <span>
          <i style={{ borderLeft: '2px dotted var(--tl-hol)', width: 0, height: 12, borderRadius: 0 }} />
          &nbsp;Public holiday
        </span>
      ) : null}
      {layout.target ? (
        <span>
          <i style={{ borderLeft: '2px solid var(--tl-accent)', width: 0, height: 12, borderRadius: 0 }} />
          &nbsp;Target finish
        </span>
      ) : null}
      {layout.today ? (
        <span>
          <i style={{ borderLeft: '2px dashed var(--tl-today)', width: 0, height: 12, borderRadius: 0 }} />
          &nbsp;Today &middot; {fmtDayYear(layout.today.iso)}
        </span>
      ) : null}
    </div>
  );
}

/**
 * One room's bars (T7.29 to T7.31).
 *
 * At most two: one Consultant, one Client, each merging that party's schedule
 * rows into a single span. The bar is a rendering; `bar.rows` is what the API
 * knows about, which is why a drag writes rows and not bars.
 */
function Bars({
  row,
  layout,
  drag,
  editable,
  onOpen,
}: {
  row: TimelineRow;
  layout: TimelineLayout;
  drag: ReturnType<typeof useScheduleDrag>;
  editable: boolean;
  onOpen: (row: TimelineRow, bar: TimelineBar) => void;
}) {
  const perDay = layout.rangeDayCount ? 100 / layout.rangeDayCount : 0;

  return (
    <>
      {row.bars.map((bar) => {
        const key = barKey(row, bar);
        const live = drag.preview?.key === key ? drag.preview : null;
        const shift = live ? live.deltaDays * perDay : 0;

        // The bar follows the cursor while it is dragged, in whole days. Both
        // edges keep the other end pinned, so the span is what will be written.
        let { left, width } = bar;
        if (live?.mode === 'move') left += shift;
        else if (live?.mode === 'start') {
          left += shift;
          width -= shift;
        } else if (live?.mode === 'end') width += shift;

        // Dragging a bar whose rows have no database id is impossible, not
        // merely refused: a workbook row has nothing to address.
        const draggable = editable && bar.addressable;
        const hint = draggable
          ? '\nDrag to move these dates; drag an edge to move one end.'
          : editable
            ? ''
            : '\nDates are fixed on this version.';

        return (
          <div
            key={key}
            className={`tl-bar ${bar.party} lane${bar.lane}${draggable ? ' draggable' : ''}${live ? ' dragging' : ''}`}
            style={{ left: `${left}%`, width: `${Math.max(width, 0.35)}%` }}
            title={bar.title + hint}
            role="button"
            tabIndex={0}
            aria-label={bar.title.split('\n')[0]}
            onClick={() => {
              if (drag.consumeClick()) return;
              onOpen(row, bar);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(row, bar);
              }
            }}
            onPointerDown={draggable ? (e) => drag.begin(e, row, bar, 'move') : undefined}
          >
            {draggable ? (
              <>
                {/* The grips are the visible affordance. On a published version,
                    or for a viewer, they are ABSENT — the bar is plainly not a
                    thing with handles, rather than a handle that does nothing. */}
                <span
                  className="grip start"
                  onPointerDown={(e) => drag.begin(e, row, bar, 'start')}
                  title={`Move the start of this ${PARTY_LABEL[bar.party].toLowerCase()} span`}
                />
                <span
                  className="grip end"
                  onPointerDown={(e) => drag.begin(e, row, bar, 'end')}
                  title={`Move the end of this ${PARTY_LABEL[bar.party].toLowerCase()} span`}
                />
              </>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

export function TimelineView({
  model,
  schedule,
  holidays,
  leave,
  editing,
}: {
  model: BlueprintModel;
  schedule: ScheduleRowWithId[];
  holidays: HolidayRow[];
  leave: LeaveRow[];
  /**
   * Present only where the caller may write AND the version is a draft. A
   * viewer, and anyone looking at a published version, gets no drag handlers and
   * no grips at all — absent, not disabled (design.md § 5, § 6).
   */
  editing?: TimelineEditing;
}) {
  const terms = useTerms();
  const [open, setOpen] = useState<{ row: TimelineRow; bar: TimelineBar } | null>(null);

  const layout = useMemo(
    () => buildTimeline({ meta: model.meta ?? {}, rooms: model.rooms, schedule, holidays, leave }),
    [model.meta, model.rooms, schedule, holidays, leave],
  );

  const drag = useScheduleDrag({ editing, rangeDayCount: layout?.rangeDayCount ?? 0 });

  if (!layout) {
    return (
      <div id="view-timeline">
        <div className="tl-panel">
          <div className="tl-eyebrow">
            <span className="dash" />
            {terms.noun('blueprint')} Capability &middot; Delivery Schedule
          </div>
          <h2>Delivery timeline</h2>
          <p className="tl-sub">
            This {terms.noun('blueprint')} has no schedule, so there is nothing to draw. A schedule
            gives each {terms.noun('room').toLowerCase()} a start and end date.
          </p>
        </div>
      </div>
    );
  }

  const leaveHeight = Math.max(46, 16 + 15 * layout.leaveLaneCount + 15);
  const leaveRowStyle = { minHeight: leaveHeight };
  const leaveTrackStyle = { height: leaveHeight };

  const roomsText = layout.roomCount
    ? countLabel(layout.roomCount, terms.noun('room').toLowerCase(), terms.plural('room').toLowerCase())
    : null;
  const progsub = [layout.snapshot ? `Snapshot ${fmtDayYear(layout.snapshot)}` : null, roomsText]
    .filter(Boolean)
    .join(' · ');
  const dCount = model.deliverableCount;

  return (
    <div id="view-timeline">
      <div className="tl-panel">
        <div className="tl-eyebrow">
          <span className="dash" />
          {terms.noun('blueprint')} Capability &middot; Delivery Schedule
        </div>
        <h2>{layout.title ? `Delivery timeline — ${layout.title}` : 'Delivery timeline'}</h2>
        <div className="tl-rule" />
        <Legend layout={layout} />
      </div>

      {/* Why a bar is or is not draggable, said once and in words. Without it,
          a published version looks the same as a draft that simply will not
          move — "visibly not draggable" has to be something you can read. */}
      <p className="tl-editnote">
        {editing
          ? `Click a bar for the ${terms.plural('deliverable').toLowerCase()} behind it. Drag it to move its dates, or drag an edge to move one end.`
          : `Click a bar for the ${terms.plural('deliverable').toLowerCase()} behind it. Dates are fixed on this version and cannot be dragged.`}
      </p>

      {drag.failure ? (
        /* Two different things, said differently. A refused write is a failure
           to fix; a publish underneath is the version itself going away, and
           telling someone "not saved" for that sends them to retry a drag on a
           version that no longer accepts one. */
        <div className="bp-toast" role="alert">
          <span>
            <b>{drag.failure.superseded ? 'This version was published.' : 'Not saved.'}</b>{' '}
            {drag.failure.what}: {drag.failure.message} The dates have been put back
            {drag.failure.superseded ? ', and this screen has been re-read' : ''}.
          </span>
          <button type="button" className="btn" onClick={drag.dismissFailure}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="tl-panel">
        <div className="tl-cardhead">
          <div>
            <h3>{layout.title || 'Programme'}</h3>
            <p className="tl-progsub">{progsub}</p>
          </div>
          <div className="tl-cardmeta">
            <b>{layout.range}</b>
            <span>
              {dCount
                ? countLabel(dCount, terms.noun('deliverable').toLowerCase(), terms.plural('deliverable').toLowerCase())
                : ''}
            </span>
          </div>
        </div>

        <div className="tl-grid two-lane">
          <div className="tl-months">
            {layout.months.map((m) => (
              <div key={m.key} style={{ width: `${m.width}%` }}>
                {m.label}
              </div>
            ))}
          </div>

          <div style={{ position: 'relative' }}>
            {layout.rows.map((row) => {
              const caption = [
                row.count
                  ? countLabel(row.count, terms.noun('deliverable').toLowerCase(), terms.plural('deliverable').toLowerCase())
                  : null,
                ...row.bars.map((b) => `${PARTY_LABEL[b.party]} ${b.days}d`),
              ];
              const sub = caption.filter(Boolean).join(' · ');
              return (
                <div className="tl-row" key={row.roomID}>
                  <div className="tl-label" title={row.title}>
                    <span className={`tl-dot ${row.status}`} />
                    <span className="nm">{row.name}</span>
                    {sub ? <span className="who">{sub}</span> : null}
                  </div>
                  <div className="tl-track">
                    <div className="tl-date" style={row.dateStyle}>
                      {row.dateLabel}
                    </div>
                    <Bars
                      row={row}
                      layout={layout}
                      drag={drag}
                      editable={Boolean(editing)}
                      onOpen={(r, b) => setOpen({ row: r, bar: b })}
                    />
                  </div>
                </div>
              );
            })}

            {layout.hasLeave ? (
              /* Leave has a row of its own (§ 7.8). It is drawn, never counted:
                 nothing on this timeline is shortened, shifted or annotated by it. */
              <div className="tl-row tl-leaverow" style={leaveRowStyle}>
                <div className="tl-label" title={layout.leaveTitle}>
                  <span className="nm">Leave</span>
                  {layout.leaveWho ? <span className="who">{layout.leaveWho}</span> : null}
                </div>
                <div className="tl-track" style={leaveTrackStyle}>
                  {layout.leaveBars.map((l, k) => (
                    <div
                      key={k}
                      className="tl-leave"
                      style={{ left: `${l.left}%`, width: `${l.width}%`, top: 16 + 15 * l.lane }}
                      title={l.title}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            <div className="tl-overlay">
              {layout.monthEdges.map((left) => (
                <div className="tl-vline" style={{ left: `${left}%` }} key={left} />
              ))}
              {[
                { b: layout.shutdown, cls: 'tl-band' },
                { b: layout.contingency, cls: 'tl-band cont' },
              ].map(({ b, cls }) =>
                b ? (
                  <div
                    className={cls}
                    style={{ left: `${b.left}%`, width: `${b.width}%` }}
                    title={`${b.label} (${b.start} – ${b.end})`}
                    key={cls}
                  >
                    {/* The band is narrow, so only the short form of the label is set
                        vertically in it; the full label is in the legend and tooltip. */}
                    <span>{b.label.split('·')[0]?.trim()}</span>
                  </div>
                ) : null,
              )}
              {layout.holidays.map((h) => (
                <div className="tl-hol" style={{ left: `${h.left}%` }} title={h.title} key={h.title} />
              ))}
              {layout.target ? (
                <div className="tl-target" style={{ left: `${layout.target.left}%` }} title={layout.target.title} />
              ) : null}
              {layout.today ? (
                <div className="tl-today" style={{ left: `${layout.today.left}%` }}>
                  {/* Labelled, so a dated rule is not read as a milestone. The caption
                      is the hover target: the rule itself has no width to hover. */}
                  <span
                    className={`tl-today-tag${layout.today.flip ? ' flip' : ''}`}
                    title={layout.today.title}
                  >
                    Today
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {open ? (
        <RoomDeliverables
          model={model}
          row={open.row}
          bar={open.bar}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}
